import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Lock, LogOut } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const AUTH_KEY = "trc_admin_auth";

const Admin = () => {
  const [authHeader, setAuthHeader] = useState(() => sessionStorage.getItem(AUTH_KEY) || "");
  const [formError, setFormError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const leadsQuery = useQuery({
    queryKey: ["admin-leads", authHeader],
    enabled: Boolean(authHeader),
    retry: false,
    queryFn: async () => {
      const res = await api.get("/admin/leads", { headers: { Authorization: authHeader } });
      return res.data;
    },
  });

  // Derive whether auth was rejected — during render, not in an effect
  const queryStatus = leadsQuery.error?.response?.status;
  const isAuthRejected = queryStatus === 401;

  // Side effect: clear stored auth on 401. AUTH_KEY is a module constant and
  // sessionStorage is a stable global — intentionally omitted from deps.
  useEffect(() => {
    if (isAuthRejected) sessionStorage.removeItem(AUTH_KEY);
  }, [isAuthRejected]);

  // Effective auth header for rendering — render login if backend rejected our header
  const effectiveAuth = isAuthRejected ? "" : authHeader;

  // Combined error string (form-side + query-side), derived during render
  const queryErrorMessage = isAuthRejected
    ? "Invalid credentials"
    : leadsQuery.error
      ? (leadsQuery.error?.response?.data?.detail || "Failed to load leads")
      : "";
  const error = formError || queryErrorMessage;

  const handleLogin = (e) => {
    e.preventDefault();
    if (!username || !password) {
      setFormError("Enter username and password");
      return;
    }
    setFormError("");
    const header = "Basic " + btoa(`${username}:${password}`);
    sessionStorage.setItem(AUTH_KEY, header);
    setAuthHeader(header);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthHeader("");
    setUsername("");
    setPassword("");
    setFormError("");
  };

  if (!effectiveAuth) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-md mx-auto px-6 py-20">
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center mb-4">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Admin sign in</h1>
            <p className="mt-1 text-sm text-slate-500">Access restricted to The River Company team.</p>
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="username" className="text-sm font-medium text-slate-700 mb-1.5 block">Username</Label>
                <Input id="username" data-testid="admin-username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
                <Input id="password" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </div>
              {error && <div className="text-sm text-red-600" data-testid="admin-login-error">{error}</div>}
              <Button type="submit" data-testid="admin-login-btn" disabled={leadsQuery.isFetching} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-md">
                {leadsQuery.isFetching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sign in
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (leadsQuery.isLoading || !leadsQuery.data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex justify-center py-32 text-slate-500"><Loader2 className="animate-spin mr-2 w-5 h-5" /> Loading leads…</div>
      </div>
    );
  }

  const leads = leadsQuery.data;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10 lg:py-16">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">Lead Submissions</h1>
            <p className="mt-2 text-slate-600">Internal view of all assessment submissions.</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn" className="border-slate-300">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>

        <h2 className="font-display text-xl font-semibold text-slate-900 mt-10 mb-3">Full Assessments ({leads.full.length})</h2>
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-full-table">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wider uppercase text-slate-500">
              <tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-left">Organization</th><th className="px-4 py-3 text-right">Score</th><th className="px-4 py-3 text-left">Band</th><th className="px-4 py-3 text-left">Recommended</th><th className="px-4 py-3 text-right">Report</th></tr>
            </thead>
            <tbody>
              {leads.full.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No submissions yet</td></tr>
              ) : leads.full.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-slate-900 font-medium">{l.lead.name}</td>
                  <td className="px-4 py-3 text-slate-600">{l.lead.email}</td>
                  <td className="px-4 py-3 text-slate-600">{l.lead.organization}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{l.overall_score}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.readiness_band}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{l.recommended_service}</td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={`/results/${l.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`view-report-${l.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#312D44] text-white text-xs font-semibold hover:bg-[#1F1B30] transition-colors whitespace-nowrap"
                    >
                      View →
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-display text-xl font-semibold text-slate-900 mt-10 mb-3">Quick Pulse Submissions ({leads.quick.length})</h2>
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-quick-table">
            <thead className="bg-slate-50 text-xs font-semibold tracking-wider uppercase text-slate-500">
              <tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th><th className="px-4 py-3 text-right">Score</th><th className="px-4 py-3 text-left">Band</th></tr>
            </thead>
            <tbody>
              {leads.quick.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">No submissions yet</td></tr>
              ) : leads.quick.map((l) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-slate-900">{l.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{l.email}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{l.overall_score}</td>
                  <td className="px-4 py-3 text-slate-600">{l.readiness_band}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
